// Libs
import {
    addDays,
    addMonths,
    addWeeks,
    addYears,
    endOfDay,
    endOfMonth,
    endOfWeek,
    endOfYear,
    isSameDay,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
} from "date-fns";
import { capitalize } from "lodash";
import moment from "moment";
import type { CompareTimeRange } from "src/components/ComparePeriodSelector/ComparePeriodSelector";

// Utils
import { type TFunction, useTranslation } from "src/design-system/core/TranslationContext";

// Types
import { CompareRangeType } from "src/types/enum/CompareRangeType";
import { RangeType } from "src/types/enum/RangeType";
import type { PartialStaticRange, StaticCompareRange, StaticRange } from "src/types/staticRange";
import type { TimeRange } from "src/types/timeRange";
import { FORMAT_DATE, type FormatterOptions } from "src/utils/formatters";

const staticRangeHandler = {
    range: {},
    isSelected(range) {
        const definedRange = this.range();
        return (
            isSameDay(range.startDate, definedRange.startDate) &&
            isSameDay(range.endDate, definedRange.endDate) &&
            (range.id === definedRange.id || range.key === definedRange.id)
        );
    },
};

export function createStaticRanges(
    ranges: PartialStaticRange<RangeType>[],
    transFn: TFunction = null
): StaticRange[] {
    return ranges.map((range) => ({
        ...staticRangeHandler,
        ...range,
        label: transFn ? transFn(range.key) : range.label,
        prefixValue: transFn ? transFn(`${range.key}-prefix`) : range.label,
        displayValue: transFn ? transFn(`${range.key}-display`) : range.label,
    }));
}

export function createStaticCompareRanges(
    ranges: PartialStaticRange<CompareRangeType>[],
    transFn: TFunction = null
): StaticCompareRange[] {
    return ranges.map((range) => ({
        ...staticRangeHandler,
        ...range,
        label: transFn ? transFn(range.key) : range.label,
        prefixValue: transFn ? transFn(`${range.key}-prefix`) : range.label,
        displayValue: transFn ? transFn(`${range.key}-display`) : range.label,
    }));
}

export const currentAndNextWeekRange: TimeRange = {
    startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
    endDate: endOfWeek(addDays(new Date(), 7), { weekStartsOn: 1 }),
    key: RangeType.selection,
    id: RangeType.currentAndNextWeek,
};

export const twoPreviousWeeks: TimeRange = {
    startDate: startOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
    endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
    key: RangeType.selection,
    id: "twoPreviousWeeks",
};

export const nextSevenDays: TimeRange = {
    startDate: new Date(),
    endDate: addDays(new Date(), 6),
    key: RangeType.selection,
    id: "nextSevenDays",
};

export const todayRange: TimeRange = {
    startDate: startOfDay(new Date()),
    endDate: new Date(),
    key: RangeType.selection,
    label: `word-link.of-this`,
    id: RangeType.today,
};

export const yesterdayRange: TimeRange = {
    startDate: addDays(startOfDay(new Date()), -1),
    endDate: addDays(endOfDay(new Date()), -1),
    key: RangeType.selection,
    label: `word-link.of-this`,
    id: RangeType.yesterday,
};

export const nextDayRange: TimeRange = {
    startDate: addDays(new Date(), 1),
    endDate: addDays(new Date(), 1),
    key: RangeType.selection,
    id: RangeType.nextDay,
};

export const nextWeekRange: TimeRange = {
    startDate: startOfWeek(addDays(new Date(), 7), { weekStartsOn: 1 }),
    endDate: endOfWeek(addDays(new Date(), 7), { weekStartsOn: 1 }),
    key: RangeType.selection,
    id: RangeType.nextWeek,
};

export const lastWeekRangeCompare: TimeRange = {
    startDate: startOfWeek(addDays(new Date(), -14), { weekStartsOn: 1 }),
    endDate: endOfWeek(addDays(new Date(), -14), { weekStartsOn: 1 }),
    key: RangeType.selection,
    id: RangeType.lastWeek,
};

export const currentWeekRange: TimeRange = {
    startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
    endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
    key: RangeType.selection,
    id: RangeType.currentWeek,
};

export const onGoingWeekRange: TimeRange = {
    startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
    endDate: new Date(),
    key: RangeType.selection,
    label: `word-link.of-this`,
    id: RangeType.onGoingWeek,
};

export const lastWeekRange: TimeRange = {
    startDate: startOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
    endDate: endOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
    key: RangeType.selection,
    label: `word-link.of-this`,
    id: RangeType.lastWeek,
};

export const weekBeforeLastWeek: TimeRange = {
    startDate: startOfWeek(addDays(new Date(), -14), { weekStartsOn: 1 }),
    endDate: endOfWeek(addDays(new Date(), -14), { weekStartsOn: 1 }),
    key: RangeType.selection,
    label: `word-link.of-this`,
    id: RangeType.weekBefore,
};

export const currentWeekRangeEndToday: TimeRange = {
    startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
    endDate: new Date(),
    key: RangeType.selection,
    id: RangeType.currentWeek,
};

export const currentMonthRange: TimeRange = {
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date()),
    key: RangeType.selection,
    label: `word-link.of-that`,
    id: RangeType.currentMonth,
};

export const onGoingMonthRange: TimeRange = {
    startDate: startOfMonth(new Date()),
    endDate: new Date(),
    key: RangeType.selection,
    label: `word-link.of`,
    id: RangeType.onGoingMonth,
};

export const currentMonthRangeEndToday: TimeRange = {
    startDate: startOfMonth(new Date()),
    endDate: new Date(),
    key: RangeType.selection,
    id: RangeType.currentMonth,
};

export const lastMonthRange: TimeRange = {
    startDate: startOfMonth(addMonths(new Date(), -1)),
    endDate: endOfMonth(addMonths(new Date(), -1)),
    key: RangeType.selection,
    label: `word-link.of`,
    id: RangeType.lastMonth,
};

export const currentYearRange: TimeRange = {
    startDate: startOfYear(new Date()),
    endDate: new Date(),
    key: RangeType.selection,
    label: `word-link.of-the`,
    id: RangeType.currentYear,
};

export const currentFullYearRange: TimeRange = {
    startDate: startOfYear(new Date()),
    endDate: endOfYear(new Date()),
    key: RangeType.selection,
    label: `word-link.of-the`,
    id: RangeType.currentYear,
};

export const last7DaysRange: TimeRange = {
    startDate: addDays(new Date(), -7),
    endDate: addDays(new Date(), -1),
    key: RangeType.selection,
    id: "last7Days",
};

export const last7DaysRangeAndToday: TimeRange = {
    startDate: addDays(new Date(), -7),
    endDate: new Date(),
    key: RangeType.selection,
    id: "last7DaysAndToday",
};

export const last15DaysRange: TimeRange = {
    startDate: addDays(new Date(), -15),
    endDate: addDays(new Date(), -1),
    key: RangeType.selection,
    id: "last15Days",
};

export const last15DaysRangeAndToday: TimeRange = {
    startDate: addDays(new Date(), -15),
    endDate: new Date(),
    key: RangeType.selection,
    id: "last15DaysAndToday",
};

export const last30DaysRange: TimeRange = {
    startDate: addDays(new Date(), -30),
    endDate: addDays(new Date(), -1),
    key: RangeType.selection,
    id: "last30Days",
};

export const last30DaysRangeAndToday: TimeRange = {
    startDate: addDays(new Date(), -30),
    endDate: new Date(),
    key: RangeType.selection,
    id: "last30DaysAndToday",
};

export const last3MonthsRange: TimeRange = {
    startDate: startOfMonth(addMonths(new Date(), -3)),
    endDate: endOfMonth(addMonths(new Date(), -1)),
    key: RangeType.selection,
    id: "lastThreeMonths",
};

export const last12MonthsRange: TimeRange = {
    startDate: startOfMonth(addMonths(new Date(), -12)),
    endDate: endOfMonth(addMonths(new Date(), -1)),
    key: RangeType.selection,
    id: "lastTwelveMonths",
};

export const lastTwoWeeksRange: TimeRange = {
    startDate: startOfWeek(addWeeks(new Date(), -2), { weekStartsOn: 1 }),
    endDate: endOfWeek(addWeeks(new Date(), -1), { weekStartsOn: 1 }),
    key: RangeType.selection,
    id: "lastTwoWeeks",
};

export const customPeriod: TimeRange = {
    startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
    endDate: new Date(),
    key: RangeType.selection,
    label: `word-link.of-the`,
    id: RangeType.custom,
};

export const lastWeekCompare: TimeRange = {
    startDate: startOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
    endDate: endOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
    key: CompareRangeType.lastWeek,
    id: CompareRangeType.lastWeek,
};

export const lastTwoWeeksCompare: TimeRange = {
    startDate: startOfWeek(addDays(new Date(), -14), { weekStartsOn: 1 }),
    endDate: endOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
    key: CompareRangeType.lastTwoWeeks,
    id: CompareRangeType.lastTwoWeeks,
};

export const lastFourWeeksCompare: TimeRange = {
    startDate: startOfWeek(addWeeks(new Date(), -4), { weekStartsOn: 1 }),
    endDate: endOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
    key: CompareRangeType.lastFourWeeks,
    id: CompareRangeType.lastFourWeeks,
};

export const weekLastYearCompare: TimeRange = {
    startDate: startOfWeek(addYears(new Date(), -1), { weekStartsOn: 1 }),
    endDate: endOfWeek(addYears(new Date(), -1), { weekStartsOn: 1 }),
    key: CompareRangeType.weekLastYearTantamount,
    id: CompareRangeType.weekLastYearTantamount,
};

export const monthLastYearCompare: TimeRange = {
    startDate: startOfMonth(addYears(new Date(), -1)),
    endDate: endOfMonth(addYears(new Date(), -1)),
    key: CompareRangeType.monthLastYearDateToDate,
    id: CompareRangeType.monthLastYearDateToDate,
};

export const findCorrectRangeByDate = (
    startDate: Date,
    endDate: Date,
    ranges: TimeRange[],
    defaultTimeRange: TimeRange
): TimeRange => {
    const filteredPeriod = ranges.find((e) => {
        return (
            moment(e.startDate).isSame(moment(startDate), "day") &&
            moment(e.endDate).isSame(moment(endDate), "day")
        );
    });
    if (!filteredPeriod) {
        return defaultTimeRange;
    }
    return filteredPeriod;
};

export const useFormatDisplayOfTimeRanges = (
    baseTimeRange: TimeRange,
    compareTimeRange: CompareTimeRange
): { baseTimeRangeLabel: string; compareTimeRangeLabel: string } => {
    const { translateCommon } = useTranslation();

    const baseTimeRangeLabel =
        baseTimeRange.key !== RangeType.selection
            ? baseTimeRange.label
            : `${capitalize(translateCommon("word-link.from"))} ${moment(
                  baseTimeRange.startDate
              ).format(
                  FORMAT_DATE
              )} ${translateCommon("word-link.to")} ${moment(baseTimeRange.endDate).format(FORMAT_DATE)}`;
    const compareTimeRangeLabel =
        compareTimeRange.key !== CompareRangeType.selectionTantamount
            ? compareTimeRange.label
            : `${capitalize(translateCommon("word-link.from"))} ${moment(
                  compareTimeRange.intervalRanges[0].startDate
              ).format(FORMAT_DATE)} ${translateCommon("word-link.to")} ${moment(
                  compareTimeRange.intervalRanges[0].endDate
              ).format(FORMAT_DATE)}`;

    return {
        baseTimeRangeLabel,
        compareTimeRangeLabel,
    };
};

export const formatDisplayRangeDateWithWords = (
    startDate: Date,
    endDate: Date,
    formatOptions: FormatterOptions
): string => {
    const { translateCommon } = formatOptions;

    if (startDate === undefined || endDate === undefined) {
        return "";
    }

    const start = {
        day: moment(startDate).format("DD"),
        month: moment(startDate).format("MM"),
        year: moment(startDate).format("YYYY"),
    };
    const end = {
        day: moment(endDate).format("DD"),
        month: moment(endDate).format("MM"),
        year: moment(endDate).format("YYYY"),
    };
    const finalSentence = `${capitalize(translateCommon("word-link.from"))} ${start.day} ${translateCommon(
        `truncateMonths.${start.month.toString()}`
    )} ${start.year} ${translateCommon("word-link.to")} ${end.day} ${translateCommon(
        `truncateMonths.${end.month.toString()}`
    )} ${end.year}`;

    return finalSentence;
};
